import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { settingsAPI, adminAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Save, ArrowRight } from 'lucide-react';

const AdminSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    company_name_fa: '',
    company_name_en: '',
    logo_url: '',
    phone: '',
    whatsapp: '',
    telegram: '',
    email: '',
    address_fa: '',
    instagram: '',
    twitter: '',
    linkedin: '',
    facebook: '',
    description_fa: '',
    meta_keywords: '',
    meta_description: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const verifyAndFetch = async () => {
      try {
        await adminAPI.verify();
        const response = await settingsAPI.get();
        setSettings(response.data);
      } catch (error) {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };
    verifyAndFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await settingsAPI.update(settings);
      toast.success('تنظیمات با موفقیت ذخیره شد');
    } catch (error) {
      toast.error('خطا در ذخیره تنظیمات');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    setSettings({ ...settings, [e.target.name]: e.target.value });
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-[#D4AF37]">در حال بارگذاری...</div></div>;
  }

  return (
    <div className="min-h-screen bg-[#121212] py-8" data-testid="admin-settings-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/admin/dashboard"
          className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#F3E5AB] transition-colors mb-6"
        >
          <ArrowRight size={20} />
          بازگشت به داشبورد
        </Link>

        <h1 className="text-3xl font-light text-white mb-8">تنظیمات کلی سایت</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="glass-effect border border-white/10 rounded-2xl p-8">
            <h2 className="text-xl font-medium text-white mb-6">اطلاعات شرکت</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-zinc-300 mb-2">نام شرکت (فارسی) *</label>
                <input
                  type="text"
                  name="company_name_fa"
                  required
                  value={settings.company_name_fa}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-300 mb-2">نام شرکت (انگلیسی) *</label>
                <input
                  type="text"
                  name="company_name_en"
                  required
                  value={settings.company_name_en}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-zinc-300 mb-2">آدرس لوگو *</label>
                <input
                  type="url"
                  name="logo_url"
                  required
                  value={settings.logo_url}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
            </div>
          </div>

          <div className="glass-effect border border-white/10 rounded-2xl p-8">
            <h2 className="text-xl font-medium text-white mb-6">اطلاعات تماس</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-zinc-300 mb-2">تلفن *</label>
                <input
                  type="text"
                  name="phone"
                  required
                  value={settings.phone}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-300 mb-2">واتساپ *</label>
                <input
                  type="text"
                  name="whatsapp"
                  required
                  value={settings.whatsapp}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-300 mb-2">تلگرام *</label>
                <input
                  type="text"
                  name="telegram"
                  required
                  value={settings.telegram}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-300 mb-2">ایمیل *</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={settings.email}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-zinc-300 mb-2">آدرس دفتر *</label>
                <input
                  type="text"
                  name="address_fa"
                  required
                  value={settings.address_fa}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
            </div>
          </div>

          <div className="glass-effect border border-white/10 rounded-2xl p-8">
            <h2 className="text-xl font-medium text-white mb-6">شبکه‌های اجتماعی</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-zinc-300 mb-2">اینستاگرام</label>
                <input
                  type="url"
                  name="instagram"
                  value={settings.instagram}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-300 mb-2">توییتر</label>
                <input
                  type="url"
                  name="twitter"
                  value={settings.twitter}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-300 mb-2">لینکدین</label>
                <input
                  type="url"
                  name="linkedin"
                  value={settings.linkedin}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-300 mb-2">فیسبوک</label>
                <input
                  type="url"
                  name="facebook"
                  value={settings.facebook}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
            </div>
          </div>

          <div className="glass-effect border border-white/10 rounded-2xl p-8">
            <h2 className="text-xl font-medium text-white mb-6">SEO</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm text-zinc-300 mb-2">توضیحات سایت</label>
                <textarea
                  name="description_fa"
                  rows="3"
                  value={settings.description_fa}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none resize-none"
                ></textarea>
              </div>
              <div>
                <label className="block text-sm text-zinc-300 mb-2">کلمات کلیدی (با کاما جدا کنید)</label>
                <input
                  type="text"
                  name="meta_keywords"
                  value={settings.meta_keywords}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                  placeholder="مهاجرت، تحصیل در خارج، ویزا"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-300 mb-2">Meta Description</label>
                <textarea
                  name="meta_description"
                  rows="2"
                  value={settings.meta_description}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none resize-none"
                  placeholder="توضیح کوتاه برای موتورهای جستجو (حداکثر 160 کاراکتر)"
                ></textarea>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            data-testid="save-settings-button"
            className="w-full px-6 py-4 bg-[#D4AF37] text-black rounded-full font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? 'در حال ذخیره...' : 'ذخیره تنظیمات'}
            <Save size={20} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminSettings;
